import { Check, Loader2, Minus, TriangleAlert } from "lucide-react";
import * as React from "react";

import { ApiError } from "@/api/client";
import { useFlowRunDag, type FlowRunNodeState } from "@/api/flow-run-dag";
import { ACTIVE_RUN_STATES, type Run } from "@/api/runs";
import { formatDuration } from "@/components/runs/run-duration";
import { useSinceSubmitted } from "@/components/runs/run-elapsed";
import { lifecycleRail, railCaption, type RailStage } from "@/components/runs/run-lifecycle-rail";
import {
  tileProgressRollup,
  tileProgressRows,
  tileProgressSummary,
  type TilePhase,
  type TileProgressRow,
} from "@/components/runs/run-tile-progress";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { VisualPanel, VisualPanelNotice } from "@/components/viz/VisualPanel";
import { cn } from "@/lib/utils";

/**
 * **The live run state (VIS/V1).** A run in flight, as a lifecycle rail over
 * the states it actually moves through plus a per-Tile progress list.
 *
 * Renders for ACTIVE runs only — `null` for the five terminal labels. That is
 * the same decision `run-lifecycle-rail.ts` documents at length (a settled
 * run's status cannot reconstruct the path it took), and it also keeps this
 * view from arguing with the Overview tab's invocation rollup below it: while a
 * run is in flight you get the live read, and once it settles the page is
 * exactly what it was before this unit.
 *
 * ---
 *
 * ## THE THREE REGISTERS, AND WHICH ONE EVERY FIGURE HERE IS IN
 *
 * Every field in this view is a claim about a run that has not finished, so
 * each one has to say which kind of claim it is. There are three and they are
 * never mixed silently:
 *
 * 1. **FACT** — read off the wire, no arithmetic beyond counting. The rail's
 *    current stage (`run.status`), "6 of 14 tiles settled" (node statuses),
 *    "×6 fan-out" (`child_count`, counted from dispatched rows), a singleton
 *    tile's duration, time since submission. These render as plain numbers.
 * 2. **ESTIMATE** — none. This view renders no estimated quantity at all,
 *    which is why a running Tile's bar is INDETERMINATE: there is no completion
 *    fraction on the wire and no honest way to derive one (see
 *    `run-tile-progress.ts`). The bar carries no `aria-valuenow` and never
 *    resolves to a percentage a reader could quote back.
 * 3. **ABSENT, WITH THE REASON** — cost. Handled below.
 *
 * ## Cost: why the field is missing, said out loud
 *
 * An in-flight run has no settled cost, and an unqualified figure beside a run
 * that is still dispatching is `flowstack#1089` finding 1 — a defect that
 * already reached `main` once, in `CostPreviewPanel`, where the "estimate"
 * badge was gated on the server's `pricing_basis` but the FIGURE was not, so an
 * older server produced a bare dollar amount with nothing qualifying it.
 *
 * The trap on this surface is that BOTH obvious fixes are wrong. An estimate
 * reads as a fact. An absent field with no explanation reads as a bug, and the
 * reader's repair for it is to go looking for the number somewhere else.
 *
 * So the panel states the absence and its reason, and — this is the part the
 * reference artboard got wrong and it matters — it does **not** promise the
 * number arrives later. The artboard's wording is "Cost is reported when the
 * run settles", and nothing in FlowStack reports a per-run cost when it settles:
 * the run detail payload carries no cost, price or currency field in any state
 * (`run-header-meta.ts` documents the same absence for the header), and the
 * cost ledger the platform does keep is TENANT-scoped and lives on the Budget
 * page. Promising a field that does not exist is the same class of error as
 * printing one that is not settled, so the copy says where cost actually lives
 * instead.
 */

/** One sentence, one place. See the file docstring for why it is worded this
 * way rather than the artboard's. */
const COST_ABSENCE =
  "No cost is shown for a run in flight: an in-flight run has no settled cost, and an unqualified figure would read as one. FlowStack's ledger is tenant-scoped — see the Budget page — and the run payload carries no per-run cost field in any state.";

export function RunLifecycle({ run }: { run: Run }): JSX.Element | null {
  const live = ACTIVE_RUN_STATES.has(run.status);
  const rail = lifecycleRail(run.status);

  // The flow-run DAG is the ONLY source of real per-Tile state, and it is
  // already fetched by `RunWorkspace` on the DAG tab under the same query key
  // (`["runs","flow-dag",runId]`). A second consumer of one key is one cache
  // entry and one poll in TanStack Query, so this view adds a subscriber, not
  // a request stream — `run-detail-polling.test.tsx` measures it rather than
  // asserting it. `enabled` is false for a standalone Tile run (the endpoint
  // 404s there by design) and for a settled run, which needs no live read.
  const dag = useFlowRunDag(run.run_id, { enabled: live && Boolean(run.flow_name) });
  const since = useSinceSubmitted(run.submitted_at, live);

  if (!live || rail === null) return null;

  const nodes: readonly FlowRunNodeState[] = dag.data?.nodes ?? [];

  return (
    <section aria-label="Live run state" className="flex flex-col gap-4">
      <VisualPanel
        as="section"
        label="Run lifecycle"
        title="Lifecycle"
        identifier={run.run_id}
        caption={railCaption(rail.path, rail.branchTaken)}
      >
        <LifecycleRailStrip stages={rail.stages} />
      </VisualPanel>

      <div
        className={cn("grid gap-4", run.flow_name && "lg:grid-cols-[minmax(0,1fr)_20rem]")}
        data-testid="run-lifecycle-body"
      >
        {run.flow_name && (
          <TilePanel nodes={nodes} loading={dag.isPending} error={dag.error ?? null} />
        )}
        <ThisRunPanel
          since={since}
          nodes={nodes}
          isFlowRun={Boolean(run.flow_name)}
          nodesReady={dag.isSuccess}
        />
      </div>
    </section>
  );
}

/** How each rail mark is drawn. A `Record` over the union so a fifth mark
 * cannot fall through to whichever branch came last — the same shape
 * `VisualPanel`'s `STATUS_MARK` uses, for the same reason.
 *
 * The four marks differ in TWO channels before any colour is read: the dot's
 * fill and its glyph or border style. `passed` is a filled tick, `current` a
 * filled spinner, `ahead` a hollow ring, `undecided` a DASHED hollow ring — so
 * the rail survives greyscale, and a reader with a CVD does not have to
 * distinguish two hues to find where the run is.
 *
 * The dash is the repaint's existing vocabulary for "not asserted": the
 * `cached` state badge carries `border-dashed` for the same reason, so a stage
 * that may not even be on this run's route is not drawn like one the route
 * does contain. Neither ring promises arrival — see `RailMark`. */
const RAIL_MARK: Record<
  RailStage["mark"],
  { dot: string; text: string; icon: React.ComponentType<{ className?: string }> | null }
> = {
  passed: {
    dot: "border-transparent bg-state-completed text-background",
    text: "text-foreground",
    icon: Check,
  },
  current: {
    dot: "border-transparent bg-state-running text-background",
    text: "text-foreground",
    icon: Loader2,
  },
  ahead: {
    dot: "border-border-subtle bg-card text-transparent",
    text: "text-muted-foreground",
    icon: null,
  },
  undecided: {
    dot: "border-dashed border-border-subtle bg-card text-transparent",
    text: "text-muted-foreground/70",
    icon: null,
  },
};

/**
 * The rail itself — an ordered list, because it IS one.
 *
 * `<ol>` rather than a row of divs: the stages have a fixed order that carries
 * the meaning, and a screen reader announcing "list, 7 items, item 4" is the
 * position information a sighted reader gets from the connector line. Each
 * stage's accessible name states its mark in words ("running, current stage"),
 * since the dot and the connector are the only sighted cues and both are
 * `aria-hidden`.
 */
function LifecycleRailStrip({ stages }: { stages: readonly RailStage[] }): JSX.Element {
  return (
    <ol className="flex list-none items-start gap-0">
      {stages.map((stage, index) => {
        const mark = RAIL_MARK[stage.mark];
        const Icon = mark.icon;
        const last = index === stages.length - 1;
        return (
          <li
            key={stage.state}
            data-rail-stage={stage.state}
            data-rail-mark={stage.mark}
            className={cn("flex min-w-0 items-start", !last && "flex-1")}
            aria-current={stage.mark === "current" ? "step" : undefined}
          >
            <div className="flex shrink-0 flex-col items-center gap-1.5">
              <span
                aria-hidden
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full border-2",
                  mark.dot
                )}
              >
                {Icon !== null && (
                  <Icon
                    className={cn(
                      "h-3 w-3",
                      stage.mark === "current" && "motion-safe:animate-arc-spin"
                    )}
                  />
                )}
              </span>
              <span className={cn("label-micro whitespace-nowrap", mark.text)}>
                {stage.label}
                <span className="sr-only">
                  {stage.mark === "current"
                    ? ", current stage"
                    : stage.mark === "passed"
                      ? ", passed"
                      : stage.mark === "undecided"
                        ? // The dashed ring and the dimmer label are the sighted
                          // cues, and neither survives being read aloud — so the
                          // announcement states the uncertainty in words rather
                          // than flattening it into "not reached yet", which is
                          // the very claim this mark exists not to make.
                          //
                          // "one of the lifecycle's branches" rather than "one
                          // of two": the count is `LIFECYCLE_PATHS`' to change,
                          // and an announcement is a poor place to keep a
                          // number nothing checks.
                          ", on one of the lifecycle's branches, not yet decided"
                        : // NOT a promise of arrival — `ahead` places the stage
                          // on the route and stays silent about reaching it,
                          // since an active run can fail out of any stage. See
                          // `RailMark`.
                          ", not reached yet"}
                </span>
              </span>
            </div>
            {!last && (
              // The connector is decoration: it repeats the neighbouring dots'
              // marks and nothing else, so it is hidden from the a11y tree
              // rather than announced as an eighth thing. `mt-2.5` centres it
              // on the 20px dot without a magic offset on the label below.
              <span
                aria-hidden
                className={cn(
                  "mx-2 mt-2.5 h-0.5 min-w-2 flex-1 rounded-full",
                  stage.mark === "passed" ? "bg-state-completed" : "bg-border-subtle"
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** The four phases a tile row is drawn in. Same two-channel rule as the rail:
 * glyph and fill both move, so none of the four is distinguished by hue alone.
 * `queued` gets no glyph at all — an outline ring — because "nothing has
 * happened here" should look like an absence rather than like a fifth kind of
 * activity. */
const PHASE_MARK: Record<
  TilePhase,
  { icon: React.ComponentType<{ className?: string }> | null; className: string; spin: boolean }
> = {
  done: {
    icon: Check,
    className: "border-transparent bg-state-completed text-background",
    spin: false,
  },
  cached: { icon: Minus, className: "border-state-cached bg-card text-state-cached", spin: false },
  active: {
    icon: Loader2,
    className: "border-transparent bg-state-running text-background",
    spin: true,
  },
  failed: {
    icon: TriangleAlert,
    className: "border-transparent bg-state-failed-system text-background",
    spin: false,
  },
  queued: { icon: null, className: "border-border-subtle bg-card text-transparent", spin: false },
};

function TilePanel({
  nodes,
  loading,
  error,
}: {
  nodes: readonly FlowRunNodeState[];
  loading: boolean;
  error: Error | null;
}): JSX.Element {
  // The distinction `useFlowRunDag`'s docstring asks callers to make. A
  // `not_a_flow_run` 404 is not a failure: the run has no reconstructible flow
  // cohort (a pre-#638 child, or a run that is not part of a flow at all), so
  // there is no per-Tile state to load and nothing went wrong. It gets the
  // NEUTRAL `unknown` mark — solid ground, so it cannot be mistaken for the
  // dashed "nothing here" pair, and no failure hue, so it does not assert a
  // severity the response does not carry (flowstack#923).
  const notAFlowRun = error instanceof ApiError && error.errorCode === "not_a_flow_run";
  const rows = React.useMemo(() => tileProgressRows(nodes), [nodes]);
  const rollup = React.useMemo(() => tileProgressRollup(nodes), [nodes]);

  return (
    <VisualPanel
      as="section"
      label="Tile progress"
      title="Tiles"
      controls={
        rows.length > 0 ? (
          <span className="text-xs tabular-nums text-muted-foreground" aria-live="polite">
            {tileProgressSummary(rollup)}
          </span>
        ) : undefined
      }
      caption={
        rows.length > 0
          ? "Counts are of TILES, not of work — tiles are nowhere near equal in cost, so 6 of 14 is not 43% done. A running tile reports no completion fraction, so its bar carries no value."
          : undefined
      }
      bodyClassName="p-0"
    >
      {notAFlowRun ? (
        <VisualPanelNotice
          status="unknown"
          title="No per-Tile state for this run."
          description="It is not part of a flow cohort the server can reconstruct, so there are no Tile rows to show. Its own lifecycle state above is unaffected."
          className="m-3"
        />
      ) : error !== null ? (
        <VisualPanelNotice
          status="error"
          title="Could not load per-Tile state."
          description="The run-scoped flow graph did not load. The run itself is unaffected — its lifecycle state above is still live."
          className="m-3"
        />
      ) : loading ? (
        <VisualPanelNotice
          status="pending"
          title="Reading the flow graph…"
          description="Per-Tile state arrives with the run-scoped DAG."
          className="m-3"
        />
      ) : rows.length === 0 ? (
        <VisualPanelNotice
          status="empty"
          title="No Tiles reported yet."
          description="The flow's nodes appear here once the runner expands the DAG for this run."
          className="m-3"
        />
      ) : (
        <Table role="table">
          <TableCaption>Per-Tile progress for this run, in pipeline order</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Tile</TableHead>
              <TableHead>Fan-out</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead align="right">Elapsed</TableHead>
              <TableHead align="right">State</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TileRow key={row.name} row={row} />
            ))}
          </TableBody>
        </Table>
      )}
    </VisualPanel>
  );
}

function TileRow({ row }: { row: TileProgressRow }): JSX.Element {
  const mark = PHASE_MARK[row.phase];
  const Icon = mark.icon;
  return (
    <TableRow interactive="hover" data-tile-phase={row.phase}>
      <TableCell>
        {/* The glyph is a sibling COLUMN of the text block, not a prefix
            inside it — so the tile name and the expansion note align under
            each other without a hand-tuned left padding that would have to be
            kept in step with the glyph's size. */}
        <div className="flex min-w-0 items-start gap-2">
          <span
            aria-hidden
            className={cn(
              "mt-1 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2",
              mark.className
            )}
          >
            {Icon !== null && (
              <Icon className={cn("h-2 w-2", mark.spin && "motion-safe:animate-arc-spin")} />
            )}
          </span>
          <div className="min-w-0">
            <div className="truncate font-mono text-xs">{row.name}</div>
            <div className="truncate font-mono text-[11px] text-muted-foreground">{row.tile}</div>
            {row.note !== null && (
              <div className="text-[11px] leading-snug text-muted-foreground">{row.note}</div>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {row.fanoutWidth === null ? (
          "—"
        ) : (
          <span className="tabular-nums">
            ×{row.fanoutWidth}
            {row.fanoutKind !== null && <span className="sr-only"> {row.fanoutKind} fan-out</span>}
          </span>
        )}
      </TableCell>
      <TableCell>
        {row.phase === "active" ? <IndeterminateBar name={row.name} /> : <span aria-hidden>—</span>}
      </TableCell>
      <TableCell align="right" className="font-mono text-xs tabular-nums">
        {formatDuration(row.durationSeconds)}
      </TableCell>
      <TableCell align="right" className="text-xs text-muted-foreground">
        {row.label}
      </TableCell>
    </TableRow>
  );
}

/**
 * A progress bar with no value, and it must stay that way.
 *
 * `role="progressbar"` WITHOUT `aria-valuenow` is the ARIA spelling of
 * indeterminate — assistive tech announces "busy", not a percentage — and it is
 * the only spelling that matches what the wire supports. The visual form is a
 * segment SWEEPING the track rather than a fill anchored at the left edge, for
 * the same reason: a bar filled to some width is read as that fraction, whether
 * or not anything claimed it.
 *
 * Under `prefers-reduced-motion` the sweep stops (`motion-safe:`) and the
 * segment sits at rest. That is deliberate and it is not a loss of information:
 * the row already carries a spinning-free glyph, the state word, and the
 * elapsed column, so the bar was never the only cue that a tile is running.
 */
function IndeterminateBar({ name }: { name: string }): JSX.Element {
  return (
    <div
      role="progressbar"
      aria-label={`${name} is running; no completion fraction is reported`}
      className="h-1.5 w-full overflow-hidden rounded-full bg-panel-well"
    >
      <div className="h-full w-1/3 rounded-full bg-state-running motion-safe:animate-progress-sweep" />
    </div>
  );
}

/**
 * The facts cluster, and the one absence.
 *
 * Every figure is a count or a clock reading; there is no derived quantity
 * here. "Cached" is worded "tiles with a cache hit" rather than "cached tiles"
 * because the backend sets `cache_hit` on a FANNED node when ANY of its
 * children was served from cache (`_project_run_scoped_nodes`), so the count is
 * of tiles that had at least one hit — a shorter label would overstate it.
 */
function ThisRunPanel({
  since,
  nodes,
  isFlowRun,
  nodesReady,
}: {
  since: string | null;
  nodes: readonly FlowRunNodeState[];
  isFlowRun: boolean;
  nodesReady: boolean;
}): JSX.Element {
  const rollup = tileProgressRollup(nodes);
  return (
    <VisualPanel as="aside" label="This run" title="This run" caption={COST_ABSENCE}>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        <div className="min-w-0">
          <dt className="label-micro text-muted-foreground">Since submitted</dt>
          {/* Not "elapsed": this span includes any time the run spent queued
              before it started, so it is an upper bound on execution time
              rather than a measure of it. `run-elapsed.ts` carries the full
              argument, and `run-header-meta.ts`'s stricter `elapsedLabel` —
              which needs BOTH timestamps and so renders nothing in flight —
              is untouched by it. */}
          <dd className="font-mono text-lg font-semibold tabular-nums">{since ?? "—"}</dd>
        </div>
        {isFlowRun && nodesReady && (
          <div className="min-w-0">
            <dt className="label-micro text-muted-foreground">Tiles with a cache hit</dt>
            <dd className="font-mono text-lg font-semibold tabular-nums">
              {rollup.cacheHits}
              <span className="text-xs font-normal text-muted-foreground">/{rollup.total}</span>
            </dd>
          </div>
        )}
      </dl>
    </VisualPanel>
  );
}
